import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshLambertMaterial,
  type BufferGeometry,
  type Material,
} from 'three';

/**
 * EN-12 Teleférico (GDD §8.3, issue #88): a thin pale cable with a support tower at each end, and a saturated
 * cabin that moves along it. The cable reads as scenery and the cabin as the hazard (GDD §14.1: decoration
 * never looks like an obstacle). The cabin model spans about 1.6 x 1.3 u; its hitbox is 1.2 x 1.0 u (70-80 %).
 */
export interface CableCarModel {
  cable: Mesh;
  towers: Group;
  cabin: Group;
  /** Places the cable between two points of the world. */
  setCable(x1: number, y1: number, x2: number, y2: number): void;
  dispose(): void;
}

export const CABLE_CAR_COLORS = { cable: 0x8f98a0, tower: 0x9aa0a6, cabin: 0xe0731a, window: 0x2b3a47, roof: 0xb85a12 } as const;

export function buildCableCar(): CableCarModel {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const paint = (color: number) => {
    const material = new MeshLambertMaterial({ color, flatShading: true });
    materials.push(material);
    return material;
  };
  const own = <T extends BufferGeometry>(geometry: T): T => {
    geometries.push(geometry);
    return geometry;
  };

  // The cable is a unit-long cylinder along y, scaled and rotated between the two ends.
  const cable = new Mesh(own(new CylinderGeometry(0.03, 0.03, 1, 4)), paint(CABLE_CAR_COLORS.cable));
  cable.name = 'enemy-en12-cable';

  const towers = new Group();
  const towerMaterial = paint(CABLE_CAR_COLORS.tower);
  for (let i = 0; i < 2; i++) towers.add(new Mesh(own(new BoxGeometry(0.2, 1, 0.2)), towerMaterial));

  const cabin = new Group();
  cabin.name = 'enemy-en12-cabin';
  const body = new Mesh(own(new BoxGeometry(1.3, 0.95, 0.7)), paint(CABLE_CAR_COLORS.cabin));
  body.position.y = -0.1;
  const window = new Mesh(own(new BoxGeometry(0.9, 0.35, 0.72)), paint(CABLE_CAR_COLORS.window));
  window.position.set(0, 0, 0);
  const roof = new Mesh(own(new BoxGeometry(1.6, 0.12, 0.8)), paint(CABLE_CAR_COLORS.roof));
  roof.position.y = 0.43;
  const hanger = new Mesh(own(new BoxGeometry(0.06, 0.2, 0.06)), paint(CABLE_CAR_COLORS.cable));
  hanger.position.y = 0.6;
  cabin.add(body, window, roof, hanger);

  const up = { x: 0, y: 1 };
  return {
    cable,
    towers,
    cabin,
    setCable(x1, y1, x2, y2) {
      const length = Math.hypot(x2 - x1, y2 - y1);
      cable.position.set((x1 + x2) / 2, (y1 + y2) / 2, -0.1);
      cable.scale.set(1, length, 1);
      cable.rotation.z = Math.atan2(x1 - x2, y2 - y1) * (up.y > 0 ? 1 : -1);
      [towers.children[0]!, towers.children[1]!].forEach((tower, i) => {
        const x = i === 0 ? x1 : x2;
        const y = i === 0 ? y1 : y2;
        // From the ground (y = 1) up to the cable end.
        tower.scale.set(1, Math.max(y - 1, 0.1), 1);
        tower.position.set(x, 1 + Math.max(y - 1, 0.1) / 2, -0.1);
      });
    },
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}
